import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Key as KeyIcon,
  Refresh as RefreshIcon,
  SmartToy as SmartToyIcon,
  DeleteOutline as RevokeIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  WarningAmber as WarningIcon,
  Bolt as BoltIcon,
  Shield as ShieldIcon,
  Check as CheckIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useLoader } from '../../contexts/LoaderContext';
import { useModal } from '../../contexts/ModalContext';

const MCP_PLUGIN = 'salesgro';
const MCP_ACCENT = '#5849EA';
const MCP_GOOD = '#0F9D58';
const MCP_INK = '#10131A';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';
const MASKED_PLACEHOLDER = '•••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••';

const copyText = async (text) => {
  if (!text) return;
  await navigator.clipboard.writeText(text);
};

const StatusPill = ({ active }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.9,
      bgcolor: active ? '#E7F7EF' : '#F0F1F5',
      color: active ? MCP_GOOD : 'text.secondary',
      fontSize: '12.5px',
      fontWeight: 700,
      px: 1.5,
      py: 0.75,
      borderRadius: '100px',
      flexShrink: 0,
    }}
  >
    {active && (
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: MCP_GOOD,
          position: 'relative',
          '@keyframes mcpPulse': {
            '0%': { opacity: 0.55, transform: 'scale(0.6)' },
            '100%': { opacity: 0, transform: 'scale(1.9)' },
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: -4,
            borderRadius: '50%',
            border: `1.5px solid ${MCP_GOOD}`,
            animation: 'mcpPulse 2s ease-out infinite',
          },
        }}
      />
    )}
    {active ? 'Active' : 'Not configured'}
  </Box>
);

const InputRow = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'stretch',
      border: '1px solid',
      borderColor: '#E6E8EE',
      borderRadius: '10px',
      bgcolor: '#FBFBFD',
      overflow: 'hidden',
      '&:focus-within': {
        borderColor: MCP_ACCENT,
        bgcolor: '#fff',
      },
    }}
  >
    {children}
  </Box>
);

const RowIconBtn = ({ title, onClick, active, children }) => (
  <Tooltip title={title}>
    <IconButton
      onClick={onClick}
      sx={{
        width: 40,
        borderRadius: 0,
        borderLeft: '1px solid #E6E8EE',
        color: active ? MCP_ACCENT : '#4B5264',
        '&:hover': { bgcolor: '#F0F1F5', color: MCP_INK },
      }}
    >
      {children}
    </IconButton>
  </Tooltip>
);

const FieldLabel = ({ label, hint }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      mb: 1,
    }}
  >
    <Typography
      sx={{
        fontSize: '11.5px',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#8A90A2',
      }}
    >
      {label}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: '11.5px', fontWeight: 500, color: '#8A90A2' }}>
        {hint}
      </Typography>
    )}
  </Box>
);

const StepText = ({ children }) => (
  <Typography
    component="div"
    sx={{
      flex: 1,
      minWidth: 0,
      fontSize: 13,
      color: '#4B5264',
      lineHeight: 1.55,
      pt: 0.25,
      wordBreak: 'break-word',
    }}
  >
    {children}
  </Typography>
);

const Bold = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 600, color: MCP_INK }}>
    {children}
  </Box>
);

const SideCard = ({ title, icon, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: '22px 24px',
      border: '1px solid #E6E8EE',
      borderRadius: '14px',
      mb: 2,
      overflow: 'visible',
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
      {icon}
      <Typography sx={{ fontSize: '13.5px', fontWeight: 700 }}>{title}</Typography>
    </Stack>
    {children}
  </Paper>
);

const McpCredentials = () => {
  const { showLoader, hideLoader } = useLoader();
  const { openSuccessModal, openErrorModal } = useModal();
  const [credentials, setCredentials] = useState({ has_credentials: false, plugin: MCP_PLUGIN });
  const [revealedSecret, setRevealedSecret] = useState('');
  const [showClientKey, setShowClientKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generateNewDialogOpen, setGenerateNewDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [inlineToast, setInlineToast] = useState('');

  const toastTimerRef = React.useRef(null);

  const showToast = (message) => {
    setInlineToast(message);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setInlineToast(''), 2200);
  };

  const clearSensitiveState = useCallback(() => {
    setRevealedSecret('');
    setShowClientKey(false);
    setShowSecret(false);
  }, []);

  useEffect(() => () => clearSensitiveState(), [clearSensitiveState]);

  const loadCredentials = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/account/mcp-credentials/');
      setCredentials(res.data);
    } catch (err) {
      openErrorModal('Failed to load credentials', err.response?.data?.error || 'Please try again.');
    } finally {
      setLoading(false);
    }
  }, [openErrorModal]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  const handleCopy = async (value, label) => {
    await copyText(value);
    showToast(`✓ ${label || 'Copied to clipboard'}`);
  };

  const handleCopySecret = async () => {
    if (!revealedSecret) return;
    await copyText(revealedSecret);
    showToast('✓ Client secret copied');
    setRevealedSecret('');
    setShowSecret(false);
  };

  const handleGenerate = async (rotateSecret = false) => {
    try {
      showLoader(rotateSecret ? 'Generating new pair…' : 'Generating credentials…');
      const res = await api.post('/account/mcp-credentials/', { rotate_secret: rotateSecret });
      setCredentials(res.data);
      if (res.data.client_secret) {
        setRevealedSecret(res.data.client_secret);
        setShowClientKey(false);
        setShowSecret(false);
      }
      setGenerateNewDialogOpen(false);
      if (rotateSecret) {
        showToast('✓ New pair generated — old credentials retired');
      } else {
        openSuccessModal('Credentials ready', res.data.message || 'MCP credentials generated successfully.');
      }
    } catch (err) {
      openErrorModal('Request failed', err.response?.data?.error || 'Failed to generate MCP credentials');
    } finally {
      hideLoader();
    }
  };

  const handleRevoke = async () => {
    try {
      showLoader('Revoking credentials…');
      await api.delete('/account/mcp-credentials/');
      setCredentials({ has_credentials: false, plugin: MCP_PLUGIN });
      clearSensitiveState();
      setRevokeDialogOpen(false);
      openSuccessModal(
        'Credentials revoked',
        'MCP credentials have been revoked. All connected automations are stopped until you generate new credentials and update Vaanik.',
      );
    } catch (err) {
      openErrorModal('Revoke failed', err.response?.data?.error || 'Failed to revoke credentials');
    } finally {
      hideLoader();
    }
  };

  const clientKey = credentials.client_key || credentials.client_id || credentials.client_api_key;
  const plugin = credentials.plugin || MCP_PLUGIN;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: MCP_ACCENT }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Banner */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #FBFAFF 0%, #F3F1FE 100%)',
          border: '1px solid #DCD6FB',
          borderRadius: '14px',
          p: '22px 26px',
          mb: 3.5,
          display: 'flex',
          gap: 2.25,
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '11px',
            bgcolor: MCP_ACCENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 4px 10px rgba(88,73,234,.25)',
          }}
        >
          <SmartToyIcon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            gap={1.5}
            sx={{ mb: 0.75 }}
          >
            <Typography sx={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>
              MCP Credentials
            </Typography>
            <StatusPill active={credentials.has_credentials} />
          </Stack>
          <Typography
            sx={{
              fontSize: '13.5px',
              color: '#4B5264',
              lineHeight: 1.55,
              maxWidth: 640,
            }}
          >
            Private keys that let Vaanik, your automation agentic system, securely act on this
            SalesGro account — search inventory, create leads, book site visits.
            <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
              Add this pair to Vaanik under{' '}
              <Box component="span" sx={{ color: MCP_INK, fontWeight: 600 }}>
                MCP Connect → salesgro plugin
              </Box>
              .
            </Box>
          </Typography>
        </Box>
      </Paper>

      <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
        <Grid item xs={12} lg={8}>
          {!credentials.has_credentials ? (
            <Paper
              elevation={0}
              sx={{
                p: 5,
                textAlign: 'center',
                border: '2px dashed #E6E8EE',
                borderRadius: '14px',
              }}
            >
              <KeyIcon sx={{ fontSize: 48, color: '#8A90A2', mb: 2 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
                No credentials yet
              </Typography>
              <Typography sx={{ fontSize: '13.5px', color: '#4B5264', maxWidth: 420, mx: 'auto', mb: 3 }}>
                Generate a client key and client secret to enable MCP automations.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleGenerate(false)}
                startIcon={<RefreshIcon />}
              >
                Generate credentials
              </Button>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                border: '1px solid #E6E8EE',
                borderRadius: '14px',
                p: '26px 28px',
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
                Your credentials
              </Typography>
              <Typography sx={{ fontSize: '12.5px', color: '#8A90A2', mb: 2.75 }}>
                One active pair per account. Generating new credentials immediately disables the old ones.
              </Typography>

              {/* Plugin */}
              <Box sx={{ mb: 2.25 }}>
                <FieldLabel label="Plugin" />
                <InputRow>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.75,
                      py: 1.35,
                      fontFamily: MONO,
                      fontSize: '13.5px',
                    }}
                  >
                    {plugin}
                    <Box
                      component="span"
                      sx={{
                        ml: 'auto',
                        fontFamily: 'inherit',
                        fontSize: '10.5px',
                        fontWeight: 600,
                        color: '#8A90A2',
                        bgcolor: '#F0F1F5',
                        px: 0.9,
                        py: 0.25,
                        borderRadius: '5px',
                      }}
                    >
                      fixed
                    </Box>
                  </Box>
                  <RowIconBtn title="Copy plugin" onClick={() => handleCopy(plugin, 'Plugin copied')}>
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </RowIconBtn>
                </InputRow>
              </Box>

              {/* Client key */}
              <Box sx={{ mb: 2.25 }}>
                <FieldLabel label="Client key" hint="identifies this account to Vaanik" />
                <InputRow>
                  <Box
                    component="input"
                    readOnly
                    value={clientKey}
                    type={showClientKey ? 'text' : 'password'}
                    sx={{
                      flex: 1,
                      border: 'none',
                      bgcolor: 'transparent',
                      px: 1.75,
                      py: 1.35,
                      fontFamily: MONO,
                      fontSize: '13.5px',
                      color: MCP_INK,
                      outline: 'none',
                      minWidth: 0,
                    }}
                  />
                  <RowIconBtn
                    title={showClientKey ? 'Hide client key' : 'Reveal client key'}
                    onClick={() => setShowClientKey((v) => !v)}
                    active={showClientKey}
                  >
                    {showClientKey ? (
                      <VisibilityOffIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <VisibilityIcon sx={{ fontSize: 16 }} />
                    )}
                  </RowIconBtn>
                  <RowIconBtn title="Copy client key" onClick={() => handleCopy(clientKey, 'Client key copied')}>
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </RowIconBtn>
                </InputRow>
              </Box>

              {/* Client secret */}
              <Box sx={{ mb: 0 }}>
                <FieldLabel
                  label="Client secret"
                  hint={
                    revealedSecret
                      ? 'visible only right now — copy it before you leave this page'
                      : undefined
                  }
                />
                <InputRow>
                  <Box
                    component="input"
                    readOnly
                    value={revealedSecret || MASKED_PLACEHOLDER}
                    type={showSecret && revealedSecret ? 'text' : 'password'}
                    sx={{
                      flex: 1,
                      border: 'none',
                      bgcolor: 'transparent',
                      px: 1.75,
                      py: 1.35,
                      fontFamily: revealedSecret ? MONO : 'inherit',
                      fontSize: '13.5px',
                      color: MCP_INK,
                      outline: 'none',
                      minWidth: 0,
                    }}
                  />
                  {revealedSecret && (
                    <>
                      <RowIconBtn
                        title={showSecret ? 'Hide client secret' : 'Reveal client secret'}
                        onClick={() => setShowSecret((v) => !v)}
                        active={showSecret}
                      >
                        {showSecret ? (
                          <VisibilityOffIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        )}
                      </RowIconBtn>
                      <RowIconBtn
                        title="Copy client secret"
                        onClick={handleCopySecret}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </RowIconBtn>
                    </>
                  )}
                </InputRow>
                {revealedSecret && (
                  <Stack direction="row" spacing={0.9} alignItems="flex-start" sx={{ mt: 1, color: '#8A90A2' }}>
                    <InfoIcon sx={{ fontSize: 13, mt: 0.25 }} />
                    <Typography sx={{ fontSize: 12, lineHeight: 1.5 }}>
                      We don&apos;t store this in plaintext, so we can&apos;t show it again later. If
                      it&apos;s lost, generate a new pair — the old one stops working immediately.
                    </Typography>
                  </Stack>
                )}
              </Box>

              <Stack
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                gap={1.25}
                sx={{ mt: 3.25, pt: 2.75, borderTop: '1px solid #F0F1F5' }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={() => setGenerateNewDialogOpen(true)}
                >
                  Generate new pair
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<RevokeIcon />}
                  onClick={() => setRevokeDialogOpen(true)}
                >
                  Revoke credentials
                </Button>
                {inlineToast && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: MCP_GOOD,
                      ml: 0.5,
                    }}
                  >
                    {inlineToast}
                  </Typography>
                )}
              </Stack>
            </Paper>
          )}
        </Grid>

        <Grid item xs={12} lg={4} sx={{ minWidth: { lg: 300 } }}>
          <Box sx={{ position: 'sticky', top: 16, minWidth: 0 }}>
            <SideCard
              title="How to connect"
              icon={<BoltIcon sx={{ fontSize: 16, color: MCP_ACCENT }} />}
            >
              <Stack spacing={0} component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {[
                  {
                    n: 1,
                    text: 'Generate credentials here.',
                  },
                  {
                    n: 2,
                    text: (
                      <>
                        Copy the <Bold>plugin name, client key, and client secret</Bold> using the icons.
                      </>
                    ),
                  },
                  {
                    n: 3,
                    text: (
                      <>
                        In Vaanik, go to <Bold>MCP Connect → salesgro</Bold> and paste the pair in.
                      </>
                    ),
                  },
                  {
                    n: 4,
                    text: (
                      <>
                        Lost the secret? Use <Bold>Generate new pair</Bold> — the old one is retired instantly.
                      </>
                    ),
                  },
                ].map(({ n, text }, idx, arr) => (
                  <Stack
                    key={n}
                    component="li"
                    direction="row"
                    alignItems="flex-start"
                    spacing={1.5}
                    sx={{ pb: idx < arr.length - 1 ? 2.25 : 0 }}
                  >
                    <Stack alignItems="center" sx={{ flexShrink: 0, width: 23 }}>
                      <Box
                        sx={{
                          width: 23,
                          height: 23,
                          borderRadius: '50%',
                          bgcolor: '#EFECFE',
                          color: MCP_ACCENT,
                          fontSize: '11.5px',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {n}
                      </Box>
                      {idx < arr.length - 1 && (
                        <Box
                          sx={{
                            width: '1px',
                            flex: 1,
                            minHeight: 20,
                            bgcolor: '#E6E8EE',
                            mt: 0.75,
                          }}
                        />
                      )}
                    </Stack>
                    <StepText>{text}</StepText>
                  </Stack>
                ))}
              </Stack>
            </SideCard>

            <SideCard
              title="Security guidelines"
              icon={<ShieldIcon sx={{ fontSize: 16, color: MCP_ACCENT }} />}
            >
              <Stack spacing={1.75}>
                {[
                  'The client secret is hashed at rest — we can\'t retrieve it for you after this session.',
                  'Values stay masked by default; reveal only when you\'re ready to copy them.',
                  'Revoking immediately disconnects every agent using this pair.',
                ].map((item) => (
                  <Stack key={item} direction="row" spacing={1.25} alignItems="flex-start">
                    <CheckIcon sx={{ fontSize: 14, color: '#8A90A2', mt: 0.25, flexShrink: 0 }} />
                    <Typography
                      component="div"
                      sx={{
                        fontSize: '12.5px',
                        color: '#4B5264',
                        lineHeight: 1.6,
                        flex: 1,
                        minWidth: 0,
                        wordBreak: 'break-word',
                      }}
                    >
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </SideCard>
          </Box>
        </Grid>
      </Grid>

      <Dialog open={generateNewDialogOpen} onClose={() => setGenerateNewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate new pair</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            A new client secret will be generated and the current one will stop working immediately.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update Vaanik MCP Connect with the new secret right away — it is shown only once.
            Automations will pause until Vaanik is updated.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setGenerateNewDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={() => handleGenerate(true)}>
            Generate new pair
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={revokeDialogOpen}
        onClose={() => setRevokeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '14px', p: 0.5 },
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 3, pb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: '#FDEEEE',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <WarningIcon sx={{ color: '#D93636', fontSize: 22 }} />
            </Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>
              Revoke MCP credentials?
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
          <Typography sx={{ fontSize: '13.5px', color: '#4B5264', lineHeight: 1.6, mb: 2 }}>
            <Box component="span" sx={{ fontWeight: 600, color: MCP_INK }}>
              Vaanik — your automation agentic system — loses access immediately.
            </Box>{' '}
            Every workflow running through it against SalesGro (searches, lead creation,
            site-visit bookings) will start failing right away.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              alignItems: 'flex-start',
              bgcolor: '#FDEEEE',
              border: '1px solid #F5C4C4',
              borderRadius: '10px',
              px: 1.75,
              py: 1.5,
            }}
          >
            <InfoIcon sx={{ fontSize: 16, color: '#D93636', mt: 0.25, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '13px', color: '#D93636', lineHeight: 1.55 }}>
              This can&apos;t be undone. To restore automations, generate a new pair and update it
              in Vaanik.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <Button variant="outlined" onClick={() => setRevokeDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<RevokeIcon sx={{ fontSize: 18 }} />}
            onClick={handleRevoke}
          >
            Revoke and stop automations
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default McpCredentials;
